import React, { useState } from "react";
import { IRecipientOfPayment, recipientOfPaymentSchema } from "../../interfaces/IRecipientOfPayment";
import { Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography } from "@mui/material";
import { AccountBalance, Add, Delete } from "@mui/icons-material";
import { RecipientOfPaymentEdit } from "./RecipientOfPaymentEdit";
import { useCreateBankAccount, useDeleteBankAccount, useUpdateBankAccount } from "../../hooks/data/data_invoice";
import { IFeedback } from "../../interfaces/IFeedback";
import { CustomSnackbar } from "../core/Snackbar";


interface IProps {
    bankAccounts: IRecipientOfPayment[];
    refetchBankAccounts: Function;
}

export const RecipientOfPaymentOverview:React.FC<IProps> = ({
    bankAccounts,
    refetchBankAccounts
}) => {
    const [editBank,setEditBank] = useState(recipientOfPaymentSchema.parse({}));
    const [isOpenEdit,setIsOpenEdit] = useState(false);
    //
    const [feedback, setFeedback] = useState<IFeedback>({ open: false, message: "", severity: "info" });
    const {isPendingUpdateBankAccount, updateBankAccount} = useUpdateBankAccount();
    const { createBankAccount,isPendingCreateBankAccount } = useCreateBankAccount();
    const { deleteBankAccount,isPendingDeleteBankAccount } = useDeleteBankAccount();

    const isPending = isPendingUpdateBankAccount || isPendingCreateBankAccount || isPendingDeleteBankAccount;

    const handleOpenEdit = (bank:IRecipientOfPayment) => {
        setEditBank(bank);
        setIsOpenEdit(true);
    }
    const handleCloseEdit = () => {
        setIsOpenEdit(false);
    }

    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();

        try {
            if (editBank.id > 0 ) {
                await updateBankAccount(editBank);
            } else {
                await createBankAccount(editBank);
            }
            setFeedback({
                open: true,
                message: "Erfolgreich gespeichert!",
                severity: "success"
            })
            refetchBankAccounts();
            setIsOpenEdit(false);
        } catch (error) {
            setFeedback({
                open: true,
                message: "Es ist ein Fehler aufgetretten!",
                severity: "error"
            })
        }
    }

    const handleDelete = async (e: React.SyntheticEvent) => {
        try {
            await deleteBankAccount(editBank);
            setFeedback({
                open: true,
                message: "Das Bankkonto wurde erfolgreich gelöscht!",
                severity: "success"
            })
            refetchBankAccounts();
            setIsOpenEdit(false);
        } catch (error) {
            setFeedback({
                open: true,
                message: "Es ist ein Fehler aufgetretten!",
                severity: "error"
            })
        }
    }


    return(
        <Box sx={{mt: 5}}>
            <CustomSnackbar feedback={feedback} setFeedback={setFeedback} />

            <Dialog
                open={isOpenEdit}
                onClose={handleCloseEdit}
            >
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <>
                            <IconButton
                                sx={{float:"right"}}
                                disabled={editBank.id < 0}
                                onClick={handleDelete}
                            >
                                <Delete/>
                            </IconButton>
                            <RecipientOfPaymentEdit
                                key={`edit-bank-${editBank.id}`}
                                editBank={editBank}
                                setEditBank={setEditBank}
                            />
                        </>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            variant="outlined"
                            onClick={handleCloseEdit}
                            disabled={isPending}
                        >
                            Abbruch
                        </Button>
                        <Button 
                            variant="contained" 
                            disabled={isPending}
                            type="submit"
                        >
                            {isPending ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <span>Speichern</span>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : (
                                "Speichern"
                            )}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>


            <Typography
                variant="h6"
                sx={{mb: 2}}
            >
                Bankkonten
                <IconButton
                    onClick={() =>
                        handleOpenEdit(recipientOfPaymentSchema.parse({}))
                    }
                >
                    <Add/>
                </IconButton>
            </Typography>
            
            <Box 
                sx={{
                    display: "flex"
                }}
            >
                {bankAccounts.map(bank =>
                    <Card 
                        variant="outlined"
                        sx={{
                            width: 300,
                            mr: 2,
                            cursor: "pointer"
                        }}
                        onClick={() =>
                            handleOpenEdit(bank)
                        }
                    >
                        <CardContent>
                            <Box sx={{
                                display: "flex",
                            }}>
                                <AccountBalance />
                                <Box 
                                    sx={{
                                        display: "block"
                                    }}
                                >
                                    <Box sx={{
                                        display: "flex",
                                        ml: 2
                                    }}>
                                        <Typography>{bank.recipient}</Typography>
                                    </Box>

                                    <Box sx={{
                                        display: "flex"
                                    }}>
                                        <Box sx={{ml: 2, width: 50}}>
                                            <Typography>IBAN</Typography>
                                        </Box>
                                        <Typography>{bank.iban}</Typography>
                                    </Box>

                                    <Box sx={{
                                        display: "flex"
                                    }}>
                                        <Box sx={{ml: 2, width: 50}}>
                                            <Typography>BIC</Typography>
                                        </Box>
                                        <Typography>{bank.bic}</Typography>
                                    </Box>
                                </Box>


                                
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </Box>
    )
}